// Hidden-variant display-name overrides for enemies, weapons and passives (the kids' /hidden
// build). Enemy names are consulted by the menu bestiary; weapon/passive names are patched
// onto the shared defs at startup. applyHiddenLabels() only ever runs on the /hidden page
// (guarded by VARIANT at the call site), so the base game's names are untouched.
//
// Phase 1 = renames only. Descriptions/effects that change with a reworked behavior (paper
// sheets, laser lines, "stronger" heart) are updated in the phase that implements them.

import { WEAPON_DEFS } from './weapons';
import { PASSIVE_DEFS } from './passives';
import { ENEMY_TYPES } from './balance';
import type { EnemyKind } from './balance';

/** Enemy display-name overrides (by enemy key). Scorpion keeps its name. */
export const HIDDEN_ENEMY_NAMES: Record<string, string> = {
  slime: 'Slime Plop',
  spore: 'Spor Sayer',
  glass: 'Grass Sharp',
  wraith: 'Ink Raid',
  vat: 'Bloated TVA',
  mortar: 'Mortar Mort',
};

/** Weapon overrides (by weapon key): name, description, and visual (projectile sprite/color).
 *  Lining coil draws straight laser lines (handled in the renderer); Files Below flings cream
 *  paper sheets instead of fire bolts. */
const HIDDEN_WEAPON_PATCHES: Record<
  string,
  { name?: string; desc?: string; projSprite?: string; color?: number }
> = {
  spark: { name: 'Lining coil', desc: 'Fires straight laser lines that chain between foes.' },
  fire: { name: 'Files Below', desc: 'Flings burning paper sheets at your foes.', projSprite: 'paper', color: 0xf0e8c0 },
};

/** Passive overrides (by passive key). Alchemists Hard makes you stronger (damage, not HP -
 *  the effect swap lives in passives.computeModifiers). */
const HIDDEN_PASSIVE_PATCHES: Record<string, { name?: string; desc?: string }> = {
  heart: { name: 'Alchemists Hard', desc: '+15% damage' },
};

let applied = false;

/** Patch weapon/passive labels + fire's projectile look for the hidden variant. Idempotent;
 *  call once on /hidden. */
export function applyHiddenLabels(): void {
  if (applied) return;
  applied = true;
  for (const key in HIDDEN_WEAPON_PATCHES) {
    const def = WEAPON_DEFS[key];
    const p = HIDDEN_WEAPON_PATCHES[key];
    if (!def) continue;
    if (p.name !== undefined) def.name = p.name;
    if (p.desc !== undefined) def.desc = p.desc;
    if (p.projSprite !== undefined) def.projSprite = p.projSprite;
    if (p.color !== undefined) def.color = p.color;
  }
  for (const key in HIDDEN_PASSIVE_PATCHES) {
    const def = PASSIVE_DEFS[key];
    const p = HIDDEN_PASSIVE_PATCHES[key];
    if (!def) continue;
    if (p.name !== undefined) def.name = p.name;
    if (p.desc !== undefined) def.desc = p.desc;
  }
  // Spor Sayer becomes a ranged musical-note shooter (was a melee swarmer). `kind` drives
  // both the standoff movement and the firing behavior, so this one field flips its role.
  (ENEMY_TYPES.spore as { kind: EnemyKind }).kind = 'ranged';
}
